<template>
    <div>
        <div
            @click="showTable = !showTable"
            class="flex justify-between cursor-pointer"
        >
            <h2>{{ model }} Table with Big List <small class="text-gray-500" v-if="loading">(Loading...)</small></h2>
            <div>
                <svg
                    v-if="!showTable"
                    class="h-4 w-4 cursor-pointer"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 9l-7 7-7-7"
                    />
                </svg>
                <svg
                    v-if="showTable"
                    class="h-4 w-4 cursor-pointer"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 15l7-7 7 7"
                    />
                </svg>
            </div>
        </div>
        <div
            v-if="showTable"
            :class="{ 'opacity-10': loading }"
        >
            <Btn
                class="mt-3"
                type="button"
                content="Reset List"
                v-if="shouldShowResetButton"
                @click="() => fetchNewEntries(true)"
            />

            <table class="min-w-full divide-y divide-gray-200 shadow my-4">
                <thead class="bg-gray-50">
                    <tr>
                        <th
                            class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            v-for="(heading, index) in Object.values(references[model].structure)"
                            :key="index"
                        >
                            {{heading}}
                        </th>
                    </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                    <draggable
                        :value="dataWithCategory"
                        @input="(data) => handleUpdateDataWithCategory(data)"
                        class="w-full contents"
                        @choose="froalaStore.disableFroala = true"
                        @unchoose="froalaStore.disableFroala = false"
                    >
                        <tr
                            v-for="(data, dataWithCategoryIndex) in dataWithCategory"
                            :key="`cb-${contentBlockIndex}-${model}-row-${data.id}`"
                        >
                            <td
                                class="px-6 py-4 whitespace-nowrap cursor-move"
                                v-for="(attribute, index) in Object.keys(references[model].structure)"
                                :key="`cb-${contentBlockIndex}-${model}-column-${index}`"
                            >
                                <div class="flex items-center">
                                    <div class="text-sm font-medium text-gray-900">
                                        <span v-if="debugStore.debugMode && data.id && index === 0" class="font-mono">
                                            [{{ model }}:{{ data.id }}]
                                        </span>
                                        {{ data[attribute] }}

                                        <span v-if="Object.keys(references[model].structure).length === index+1 && isDeletable" @click="() => deleteItem(dataWithCategoryIndex)"
                                            class="text-blue-600 text-gray-400 hover:text-gray-600 cursor-pointer ml-4 transition duration-150 transform hover:scale-105">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                class="h-5 w-5 inline"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                    stroke-width="2"
                                                    d="M6 18L18 6M6 6l12 12"
                                                />
                                            </svg>
                                        </span>

                                    </div>
                                </div>
                            </td>
                        </tr>
                    </draggable>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import draggable from "vuedraggable";
import References from "./references";
import {useDebugStore} from "../../stores/debug";
import {useFroalaStore} from "../../stores/froala";

export default {
    name: "TableWithBigList",
    components: { draggable },
    props: {
        model: {
            type: String,
            required: true,
        },
        languageId: {
            type: Number,
            required: true,
        },
        listOptions: {
            type: Object,
            required: false,
        },
        value: Array,
        contentBlockIndex: Number,
        shouldShowResetButton: {
            type: Boolean,
            required: false,
            default: true
        }
    },
    data() {
        return {
            data: [],
            dataWithCategory: [],
            showTable: false,
            loading: false,
            isDeletable: false,
            debugStore: useDebugStore(),
            froalaStore: useFroalaStore(),
        };
    },
    mounted: function () {
        this.setData(this.value);
        this.fetchNewEntries(false);
    },
    methods: {
        handleUpdateDataWithCategory(data) {
            this.dataWithCategory = data;
        },
        fetchNewEntries(shouldUpdate=false) {
            this.loading = true;
            axios
            .get(References[this.model].getRoute, {
                params: {
                    ...(this.listOptions || {}),
                    language: this.languageId ?? 1,
                }
            })
            .then((resp) => {
                this.setData(resp.data.data, shouldUpdate);
                this.loading = false;
            })
            .catch((error) => {
                console.log("err", error);
                this.loading = false;
            });
        },
        setData(results, shouldUpdate=false){
            this.showDeleteIcon();

            if (this.value.length && !shouldUpdate) {
                this.dataWithCategory = this.value;
            } else {
                if (!this.listOptions?.listAll) {
                    let data = results;
                    if (this.data.length) {
                        const dataIds = this.data.map(datum => datum.id);
                        data = data.filter(datum => {
                            return !dataIds.includes(datum.id);
                        });
                    }
                    this.data.push(...data);
                    this.dataWithCategory = [];
                    this.dataWithCategory.push(...this.data.filter(
                        (document) => document.category_id || document.category?.id
                    ));
                } else {
                    this.dataWithCategory = results;
                }
            }
        },
        showDeleteIcon() {
            if (this.listOptions?.listAll && (this.listOptions?.listAll == 1 || this.listOptions?.listLimit)) {
                this.isDeletable = false;
            } else {
                this.isDeletable = true;
            }
        },
        deleteItem(index) {
            this.dataWithCategory.splice(index, 1);
        }
    },
    computed: {
        references(){
            return References;
        },
    },
    watch: {
        dataWithCategory(newValue) {
            this.$emit('input', newValue);
        },
        listOptions: {
            handler () {
                this.fetchNewEntries(true);
            },
            deep: true,
        },
    }
};
</script>
